import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { IsChallengeTriggeredResponse } from './models/IsChallengeTriggered.response';
import { IsUserLoggedResponse } from './models/IsUserLogged.response';
import { SocialLoginResponse } from './models/SocialLogin.response';
import { RegisterByEmailDto } from './models/registerByEmail.dto';
import { RegisterByGoogleDto } from './models/registerByGoogle.dto';
import { ResetPasswordError } from './models/errors';
import { CheckIfEmailExistsDto } from './models/checkIfEmailExists.dto';
import { catchResponseError } from '@portal-front/util-http-layer';
import { AUTH_CONTEXT_CONFIG, AuthContextConfig, CaptchaProvider } from '@portal-front/portal-auth-shared-util-config';
import { FeatureFlagsService } from '@portal-front/feature-flags/angular';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    }),
  };

  constructor(
    private http: HttpClient,
    private featureFlagsService: FeatureFlagsService,
    @Optional() @Inject(AUTH_CONTEXT_CONFIG) private readonly authContextConfig?: AuthContextConfig,
  ) {}

  private get captchaProvider(): CaptchaProvider {
    return this.authContextConfig?.captchaProvider ?? 'recaptcha';
  }

  private get isNewCaptchaValidationEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled('ff-new-captcha-validation');
  }

  private createHttpOptions(captchaToken?: string, formSource?: 'login' | 'register' | 'forgot-password') {
    if (!captchaToken || !this.isNewCaptchaValidationEnabled) {
      return this.httpOptions;
    }

    const providerHeaderValue = this.captchaProvider === 'recaptcha' ? 'google' : 'cloudflare';

    let headers = this.httpOptions.headers
      .set('X-Recaptcha-Token', captchaToken)
      .set('X-Recaptcha-Provider', providerHeaderValue);

    if (formSource) {
      headers = headers.set('X-Form-Source', formSource);
    }

    return {
      ...this.httpOptions,
      headers,
    };
  }

  login(email: string, password: string, rememberMe: boolean, recaptchaToken: string): Observable<any> {
    const baseParams = {
      _username: email,
      _password: password,
      _accept_rules: 'on',
      ...(rememberMe ? { _remember_me: 'on' } : {}),
    };

    const paramsWithCaptcha =
      !this.isNewCaptchaValidationEnabled && this.captchaProvider === 'recaptcha'
        ? { ...baseParams, 'g-recaptcha-response': recaptchaToken }
        : baseParams;

    const params = new HttpParams({ fromObject: paramsWithCaptcha });
    return this.http.post('/auth', params, this.createHttpOptions(recaptchaToken, 'login'));
  }

  isChallengeTriggered(): Promise<boolean> {
    return firstValueFrom(
      this.http.get<IsChallengeTriggeredResponse>('/is-challenge-triggered').pipe(
        map((res) => res.isChallengeTriggered),
        catchError(() => of(true)), // W przypadku błędu - wymagaj captcha dla bezpieczeństwa
      ),
    );
  }

  getIsUserLoggedInfo(): Promise<IsUserLoggedResponse | null> {
    return firstValueFrom(
      this.http.get<IsUserLoggedResponse>('/api/is-user-logged', this.httpOptions).pipe(catchError(() => of(null))),
    );
  }

  googleLogin(idToken: string): Promise<SocialLoginResponse> {
    const params = new HttpParams({
      fromObject: {
        token: idToken,
        type: 'google-id-token',
      },
    });
    return firstValueFrom(
      this.http.post('/social/login', params, this.httpOptions).pipe(
        map((res) => res as SocialLoginResponse),
        catchError(({ error, message }: HttpErrorResponse) =>
          of({
            success: false,
            code: 401,
            message: error?.message || message,
            token: idToken,
          } as SocialLoginResponse),
        ),
      ),
    );
  }

  public sendResetPasswordRequest(email: string, captchaToken: string): Promise<undefined> {
    const baseParams = { email };

    const paramsWithCaptcha =
      !this.isNewCaptchaValidationEnabled && this.captchaProvider === 'recaptcha'
        ? { ...baseParams, 'g-recaptcha-response': captchaToken }
        : baseParams;

    const params = new HttpParams({ fromObject: paramsWithCaptcha });

    return firstValueFrom(
      this.http.post('/api/reset-password', params, this.createHttpOptions(captchaToken, 'forgot-password')).pipe(
        map(() => undefined),
        catchResponseError(() => {
          throw new ResetPasswordError();
        }),
      ),
    );
  }

  public registerByEmail({
    email,
    acceptTerms,
    acceptMarketing,
    password,
    phoneNumber,
    isPhotographer,
  }: RegisterByEmailDto): Observable<undefined> {
    const data = {
      email,
      acceptRules: acceptTerms,
      acceptRules1: acceptMarketing,
      invoice: false,
      password,
      repassword: password,
      phoneNumber: phoneNumber,
      ...(isPhotographer && { isPhotographer }),
    };

    return this.http.post('/api/register', data).pipe(map(() => undefined));
  }

  public registerByGoogle(dto: RegisterByGoogleDto): Observable<undefined> {
    const params = new HttpParams({
      fromObject: {
        token: dto.googleToken,
        token_type: 'id_token',
        e_invoice_agree: dto.termsAccepted,
        marketing_agree: dto.marketingAccepted,
        phone_number: dto.phoneNumber ?? '',
        ...(dto.isPhotographer && { isPhotographer: dto.isPhotographer }),
      },
    });

    return this.http.post('/google-register', params, this.httpOptions).pipe(map(() => undefined));
  }

  public checkIfEmailExists(dto: CheckIfEmailExistsDto): Observable<undefined> {
    const result = this.http.get<undefined>(
      `/exists/email/${dto.email}/${dto.captchaToken}`,
      this.createHttpOptions(dto.captchaToken, 'register'),
    );
    return result;
  }
}
